import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { apiService } from '../services/apiService';
import { Task, TaskAttachment, TaskComment, TaskPriority, TaskStatus } from '../types/Task';
import { formatTaskDateTime } from '../utils/taskDueDate';
import { TASK_PRIORITY_VALUES, getTaskPriorityLabel } from '../utils/taskPriority';
import FieldHelpPopover from './FieldHelpPopover';
import './LoginModal.css';
import {
  buildTaskPayload,
  createEmptyChecklistItem,
  DUE_DATE_PRESET_OPTIONS,
  getDueDateWarning,
  getDueDatePresetValue,
  MAX_ATTACHMENT_FILES,
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_FILE_SIZE_LABEL,
  MAX_CHECKLIST_ITEM_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  MAX_FUTURE_DUE_DATE_DAYS,
  MAX_LABEL_LENGTH,
  MAX_LABELS,
  MAX_TITLE_LENGTH,
  TaskFormValues,
  validateTaskForm,
} from './taskFormValidation';

export interface TaskFormSaveResult {
  action: 'created' | 'updated' | 'deleted';
  taskTitle?: string;
  taskId?: number;
}

interface TaskFormModalProps {
  isOpen: boolean;
  taskId?: number;
  initialTask?: Task | null;
  prefillTask?: Task | null;
  onClose: () => void;
  onSaved: (result: TaskFormSaveResult) => Promise<void> | void;
}

const MAX_COMMENT_LENGTH = 500;

const EMPTY_FORM: TaskFormValues = {
  title: '',
  description: '',
  status: TaskStatus.TODO,
  priority: TaskPriority.MEDIUM,
  dueDate: '',
  labels: '',
  checklistItems: [],
};

const PRIORITY_HELP_ITEMS: Array<{ priority: TaskPriority; description: string }> = [
  {
    priority: TaskPriority.LOW,
    description: 'Flexible work that can wait if more important tasks appear.',
  },
  {
    priority: TaskPriority.MEDIUM,
    description: 'Standard day-to-day work and the default choice for most tasks.',
  },
  {
    priority: TaskPriority.HIGH,
    description: 'Important work that should be handled soon to avoid delays.',
  },
  {
    priority: TaskPriority.URGENT,
    description: 'Time-sensitive work that must have a due date and cannot use a past date.',
  },
];

const normalizeCommentText = (value: string) => value.trim();

const getCommentTimestamp = (comment: TaskComment) => comment.updatedAt || comment.createdAt;

const getErrorMessage = (value: unknown, fallbackMessage: string) => {
  if (value instanceof Error && value.message) {
    return value.message;
  }

  return fallbackMessage;
};

const formatAttachmentSize = (fileSizeBytes: number) => {
  if (fileSizeBytes >= MAX_ATTACHMENT_FILE_SIZE_BYTES) {
    const sizeInMegabytes = fileSizeBytes / MAX_ATTACHMENT_FILE_SIZE_BYTES;
    return `${Number.isInteger(sizeInMegabytes) ? sizeInMegabytes : sizeInMegabytes.toFixed(1)} MB`;
  }

  if (fileSizeBytes >= 1024) {
    return `${Math.round(fileSizeBytes / 1024)} KB`;
  }

  return `${fileSizeBytes} B`;
};

const getAttachmentValidationError = (
  selectedFiles: File[],
  existingAttachmentCount: number,
  pendingAttachmentCount: number
) => {
  if (selectedFiles.length === 0) {
    return null;
  }

  if (existingAttachmentCount + pendingAttachmentCount + selectedFiles.length > MAX_ATTACHMENT_FILES) {
    return `You can attach up to ${MAX_ATTACHMENT_FILES} files per task.`;
  }

  const emptyFile = selectedFiles.find((file) => file.size === 0);
  if (emptyFile) {
    return `File '${emptyFile.name}' is empty and cannot be uploaded.`;
  }

  const oversizedFile = selectedFiles.find((file) => file.size > MAX_ATTACHMENT_FILE_SIZE_BYTES);
  if (oversizedFile) {
    return `File '${oversizedFile.name}' exceeds the ${MAX_ATTACHMENT_FILE_SIZE_LABEL} limit.`;
  }

  return null;
};

const formatDateTime = (value: string | undefined) => {
  return formatTaskDateTime(value, 'Not set');
};

export const TaskFormModal: React.FC<TaskFormModalProps> = ({
  isOpen,
  taskId,
  initialTask,
  prefillTask,
  onClose,
  onSaved,
}) => {
  const titleId = useId();
  const formFieldBaseId = useId();
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const isEditMode = typeof taskId === 'number';
  const [fieldIdVersion, setFieldIdVersion] = useState(0);
  const previousIsOpenRef = useRef(false);

  const [formData, setFormData] = useState<TaskFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskDetails, setTaskDetails] = useState<Task | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [attachmentsMarkedForRemoval, setAttachmentsMarkedForRemoval] = useState<TaskAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      setFieldIdVersion((current) => current + 1);
    }

    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  const getFormFieldId = (fieldName: string) => `${formFieldBaseId}-open-${fieldIdVersion}-${fieldName}`;

  const getFormValuesFromTask = (task: Task): TaskFormValues => ({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority || TaskPriority.MEDIUM,
      dueDate: task.dueDate || '',
      labels: (task.labels || []).join(', '),
      checklistItems: (task.checklistItems || []).map((item) => ({
        text: item.text,
        done: item.done,
      })),
    });

  const setFormValuesFromTask = useCallback((task: Task) => {
    setFormData(getFormValuesFromTask(task));
  }, []);

  const handleMutationError = (mutationError: unknown, fallbackMessage: string) => {
    if (mutationError instanceof Error && mutationError.message) {
      setCommentError(mutationError.message);
      return;
    }

    setCommentError(fallbackMessage);
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      setError(null);
      setLoading(false);
      setLoadingTask(false);
      setTaskDetails(null);
      setPendingAttachments([]);
      setAttachmentsMarkedForRemoval([]);
      setAttachmentError(null);
      setNewCommentText('');
      setEditingCommentId(null);
      setEditingText('');
      setCommentSubmitting(false);
      setCommentError(null);
      return;
    }

    if (!isEditMode) {
      setPendingAttachments([]);
      setAttachmentsMarkedForRemoval([]);
      setAttachmentError(null);
      setFormData(prefillTask ? getFormValuesFromTask(prefillTask) : EMPTY_FORM);
      setTaskDetails(null);
      return;
    }

    if (initialTask) {
      setPendingAttachments([]);
      setAttachmentsMarkedForRemoval([]);
      setAttachmentError(null);
      setTaskDetails(initialTask);
      setFormValuesFromTask(initialTask);

      if (initialTask.comments !== undefined && initialTask.attachments !== undefined) {
        return;
      }
    }

    let cancelled = false;

    const loadTask = async () => {
      try {
        setLoadingTask(true);
        setError(null);
        const task = await apiService.getTaskById(taskId);
        if (cancelled) {
          return;
        }

        setTaskDetails(task);
        setFormValuesFromTask(task);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load task. Please try again.');
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          setLoadingTask(false);
        }
      }
    };

    void loadTask();

    return () => {
      cancelled = true;
    };
  }, [initialTask, isEditMode, isOpen, prefillTask, setFormValuesFromTask, taskId]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading && !commentSubmitting) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commentSubmitting, isOpen, loading, onClose]);

  const validateForm = () => {
    const newErrors = validateTaskForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const visibleAttachments = (taskDetails?.attachments || []).filter(
    (attachment) => !attachmentsMarkedForRemoval.some((markedAttachment) => markedAttachment.id === attachment.id)
  );

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    const fieldsToClear = new Set([name]);

    if (name === 'title' || name === 'description') {
      fieldsToClear.add('description');
    }

    if (name === 'priority' || name === 'dueDate') {
      fieldsToClear.add('dueDate');
    }

    if ([...fieldsToClear].some((field) => errors[field])) {
      setErrors((current) => {
        const next = { ...current };
        fieldsToClear.forEach((field) => {
          delete next[field];
        });
        return next;
      });
    }
  };

  const clearDueDateError = () => {
    if (!errors.dueDate) {
      return;
    }

    setErrors((current) => {
      const next = { ...current };
      delete next.dueDate;
      return next;
    });
  };

  const handleDueDatePresetSelect = (daysFromToday: number) => {
    setFormData((current) => ({
      ...current,
      dueDate: getDueDatePresetValue(daysFromToday),
    }));
    clearDueDateError();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = buildTaskPayload(formData);
      const taskTitle = payload.title.trim();
      let savedTaskId = isEditMode ? taskId : undefined;
      let createdTaskId: number | undefined;

      if (isEditMode && taskId) {
        await apiService.updateTask(taskId, payload);
      } else {
        const createdTask = await apiService.createTask(payload);
        savedTaskId = createdTask.id;
        createdTaskId = createdTask.id;
      }

      if (savedTaskId && (pendingAttachments.length > 0 || attachmentsMarkedForRemoval.length > 0)) {
        try {
          for (const attachment of attachmentsMarkedForRemoval) {
            await apiService.deleteAttachment(savedTaskId, attachment.id);
          }

          await apiService.uploadAttachments(savedTaskId, pendingAttachments);
        } catch (attachmentMutationError) {
          if (!isEditMode && createdTaskId) {
            try {
              await apiService.deleteTask(createdTaskId);
            } catch (rollbackError) {
              console.error('Failed to roll back created task after attachment upload error.', rollbackError);
            }

            throw new Error(getErrorMessage(
              attachmentMutationError,
              'Failed to upload attachments. Task creation was rolled back.'
            ));
          }

          if (savedTaskId) {
            try {
              const refreshedTask = await apiService.getTaskById(savedTaskId);
              setTaskDetails(refreshedTask);
              setFormValuesFromTask(refreshedTask);
            } catch (refreshError) {
              console.error('Failed to refresh task after attachment mutation error.', refreshError);
            }
          }

          setPendingAttachments([]);
          setAttachmentsMarkedForRemoval([]);

          throw new Error(getErrorMessage(
            attachmentMutationError,
            'Task saved, but attachment changes failed. Review the latest task state and try again.'
          ));
        }
      }

      setPendingAttachments([]);
      setAttachmentsMarkedForRemoval([]);
      setAttachmentError(null);

      await onSaved({
        action: isEditMode ? 'updated' : 'created',
        taskTitle,
        taskId: savedTaskId,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save task. Please try again.'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !taskId) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const taskTitle = (taskDetails?.title || initialTask?.title || formData.title).trim();
      await apiService.deleteTask(taskId);
      await onSaved({
        action: 'deleted',
        taskTitle,
        taskId,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete task. Please try again.'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearChecklistError = () => {
    if (!errors.checklistItems) {
      return;
    }

    setErrors((current) => {
      const next = { ...current };
      delete next.checklistItems;
      return next;
    });
  };

  const handleChecklistTextChange = (index: number, value: string) => {
    setFormData((current) => ({
      ...current,
      checklistItems: current.checklistItems.map((item, itemIndex) => (
        itemIndex === index ? { ...item, text: value } : item
      )),
    }));
    clearChecklistError();
  };

  const handleChecklistDoneChange = (index: number, done: boolean) => {
    setFormData((current) => ({
      ...current,
      checklistItems: current.checklistItems.map((item, itemIndex) => (
        itemIndex === index ? { ...item, done } : item
      )),
    }));
    clearChecklistError();
  };

  const handleAddChecklistItem = () => {
    setFormData((current) => ({
      ...current,
      checklistItems: [...current.checklistItems, createEmptyChecklistItem()],
    }));
    clearChecklistError();
  };

  const handleRemoveChecklistItem = (index: number) => {
    setFormData((current) => ({
      ...current,
      checklistItems: current.checklistItems.filter((_, itemIndex) => itemIndex !== index),
    }));
    clearChecklistError();
  };

  const clearAttachmentInput = () => {
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const handleAttachmentSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    const validationError = getAttachmentValidationError(
      selectedFiles,
      visibleAttachments.length,
      pendingAttachments.length
    );

    if (validationError) {
      setAttachmentError(validationError);
      clearAttachmentInput();
      return;
    }

    setPendingAttachments((current) => [...current, ...selectedFiles]);
    setAttachmentError(null);
    clearAttachmentInput();
  };

  const handleRemovePendingAttachment = (index: number) => {
    setPendingAttachments((current) => current.filter((_, fileIndex) => fileIndex !== index));
    setAttachmentError(null);
    clearAttachmentInput();
  };

  const handleMarkAttachmentForRemoval = (attachment: TaskAttachment) => {
    setAttachmentsMarkedForRemoval((current) => [...current, attachment]);
    setAttachmentError(null);
  };

  const handleUndoAttachmentRemoval = (attachmentId: number) => {
    setAttachmentsMarkedForRemoval((current) => current.filter((attachment) => attachment.id !== attachmentId));
    setAttachmentError(null);
  };

  const handleAddComment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isEditMode || !taskId) {
      return;
    }

    const normalizedText = normalizeCommentText(newCommentText);

    if (!normalizedText) {
      setCommentError('Comment text is required.');
      return;
    }

    if (normalizedText.length > MAX_COMMENT_LENGTH) {
      setCommentError(`Comment must be ${MAX_COMMENT_LENGTH} characters or fewer.`);
      return;
    }

    try {
      setCommentSubmitting(true);
      setCommentError(null);
      const createdComment = await apiService.addComment(taskId, normalizedText);
      setTaskDetails((currentTask) => (
        currentTask
          ? {
              ...currentTask,
              comments: [...(currentTask.comments || []), createdComment],
            }
          : currentTask
      ));
      setNewCommentText('');
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to add comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleSaveComment = async (commentId: number) => {
    if (!isEditMode || !taskId) {
      return;
    }

    const normalizedText = normalizeCommentText(editingText);

    if (!normalizedText) {
      setCommentError('Comment text is required.');
      return;
    }

    if (normalizedText.length > MAX_COMMENT_LENGTH) {
      setCommentError(`Comment must be ${MAX_COMMENT_LENGTH} characters or fewer.`);
      return;
    }

    try {
      setCommentSubmitting(true);
      setCommentError(null);
      const updatedComment = await apiService.updateComment(taskId, commentId, normalizedText);
      setTaskDetails((currentTask) => (
        currentTask
          ? {
              ...currentTask,
              comments: (currentTask.comments || []).map((comment) => (
                comment.id === commentId ? updatedComment : comment
              )),
            }
          : currentTask
      ));
      setEditingCommentId(null);
      setEditingText('');
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to update comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!isEditMode || !taskId) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      setCommentSubmitting(true);
      setCommentError(null);
      await apiService.deleteComment(taskId, commentId);
      setTaskDetails((currentTask) => (
        currentTask
          ? {
              ...currentTask,
              comments: (currentTask.comments || []).filter((comment) => comment.id !== commentId),
            }
          : currentTask
      ));

      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditingText('');
      }
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to delete comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const comments = taskDetails?.comments ?? [];
  const totalAttachmentCount = visibleAttachments.length + pendingAttachments.length;
  const isBusy = loading || commentSubmitting;
  const dueDateWarning = getDueDateWarning(formData.dueDate, formData.priority);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(event) => {
        if (!isBusy && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="modal-content task-form-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-header">
          <h2 id={titleId}>{isEditMode ? 'Edit Task' : 'Create Task'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close task form" disabled={isBusy}>
            ×
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {loadingTask ? (
          <div className="loading">Loading task...</div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="task-form">
              <div className="form-group">
                <label htmlFor={getFormFieldId('title')}>
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id={getFormFieldId('title')}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter task title"
                  maxLength={MAX_TITLE_LENGTH}
                  disabled={loading}
                  className={errors.title ? 'error-input' : ''}
                />
                {errors.title && <span className="error-text">{errors.title}</span>}
                <span className="char-count">{formData.title.length}/{MAX_TITLE_LENGTH}</span>
              </div>

              <div className="form-group">
                <label htmlFor={getFormFieldId('description')}>Description</label>
                <textarea
                  id={getFormFieldId('description')}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter task description"
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  rows={5}
                  disabled={loading}
                  className={errors.description ? 'error-input' : ''}
                />
                {errors.description && <span className="error-text">{errors.description}</span>}
                <span className="char-count">{formData.description.length}/{MAX_DESCRIPTION_LENGTH}</span>
              </div>

              <div className="form-group">
                <label htmlFor={getFormFieldId('status')}>Status</label>
                <select
                  id={getFormFieldId('status')}
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="TODO">TODO</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="DONE">Done</option>
                </select>
              </div>

              <fieldset className="form-group form-group-radio-fieldset">
                <div className="form-label-row">
                  <span id={getFormFieldId('priority-label')} className="form-label-text">Priority</span>
                  <FieldHelpPopover
                    triggerLabel="Open level guide"
                    popoverLabel="Priority guidance"
                    title="Priority guidance"
                  >
                    <ul className="field-help-list">
                      {PRIORITY_HELP_ITEMS.map((item) => (
                        <li key={item.priority}>
                          <strong>{getTaskPriorityLabel(item.priority)}:</strong> {item.description}
                        </li>
                      ))}
                    </ul>
                  </FieldHelpPopover>
                </div>
                <div className="priority-radio-group" role="radiogroup" aria-labelledby={getFormFieldId('priority-label')}>
                  {TASK_PRIORITY_VALUES.map((priority) => (
                    <label
                      key={priority}
                      className={`priority-radio-option${formData.priority === priority ? ' is-selected' : ''}${loading ? ' is-disabled' : ''}`}
                    >
                      <input
                        type="radio"
                        name="priority"
                        value={priority}
                        checked={formData.priority === priority}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <span>{getTaskPriorityLabel(priority)}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="form-group">
                <div className="form-label-row">
                  <label htmlFor={getFormFieldId('due-date')}>Due Date</label>
                  <FieldHelpPopover
                    triggerLabel="Open date rules"
                    popoverLabel="Due date guidance"
                    title="Due date guidance"
                  >
                    <ul className="field-help-list">
                      {DUE_DATE_PRESET_OPTIONS.map((preset) => (
                        <li key={preset.key}>
                          <strong>{preset.label}:</strong> fills the date automatically.
                        </li>
                      ))}
                      <li>Past dates show an overdue warning for non-urgent tasks, but you can still save them.</li>
                      <li>Urgent tasks require a due date and cannot use a past date.</li>
                      <li>Dates more than {MAX_FUTURE_DUE_DATE_DAYS} days ahead are blocked.</li>
                    </ul>
                  </FieldHelpPopover>
                </div>
                <div className="task-form-date-presets" role="group" aria-label="Quick date presets">
                  {DUE_DATE_PRESET_OPTIONS.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      className="btn btn-secondary task-form-date-preset"
                      onClick={() => handleDueDatePresetSelect(preset.daysFromToday)}
                      disabled={loading}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <input
                  type="date"
                  id={getFormFieldId('due-date')}
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  disabled={loading}
                  className={errors.dueDate ? 'date-input error-input' : 'date-input'}
                />
                {errors.dueDate && <span className="error-text">{errors.dueDate}</span>}
                {dueDateWarning && <span className="task-form-warning">{dueDateWarning}</span>}
              </div>

              <div className="form-group">
                <label htmlFor={getFormFieldId('labels')}>Labels</label>
                <input
                  type="text"
                  id={getFormFieldId('labels')}
                  name="labels"
                  value={formData.labels}
                  onChange={handleChange}
                  placeholder="api, urgent, frontend"
                  maxLength={MAX_LABELS * (MAX_LABEL_LENGTH + 2)}
                  disabled={loading}
                  className={errors.labels ? 'error-input' : ''}
                />
                {errors.labels && <span className="error-text">{errors.labels}</span>}
                <span className="help-text">Comma-separated labels, up to {MAX_LABELS} entries.</span>
              </div>

              <div className="form-group">
                <div className="task-checklist-header">
                  <div>
                    <label>Checklist</label>
                    <span className="help-text">Add subtasks with text and a done state.</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary task-checklist-add"
                    onClick={handleAddChecklistItem}
                    disabled={loading}
                  >
                    Add item
                  </button>
                </div>

                {formData.checklistItems.length === 0 ? (
                  <p className="help-text">No checklist items yet.</p>
                ) : (
                  <ol className="task-checklist-list">
                    {formData.checklistItems.map((item, index) => (
                      <li key={`task-checklist-${index}`} className="task-checklist-row">
                        <label className="task-checklist-toggle">
                          <input
                            type="checkbox"
                            checked={item.done}
                            onChange={(event) => handleChecklistDoneChange(index, event.target.checked)}
                            disabled={loading}
                          />
                          <span>Done</span>
                        </label>
                        <input
                          type="text"
                          value={item.text}
                          onChange={(event) => handleChecklistTextChange(index, event.target.value)}
                          placeholder={`Checklist item ${index + 1}`}
                          maxLength={MAX_CHECKLIST_ITEM_LENGTH}
                          disabled={loading}
                          className={errors.checklistItems ? 'error-input' : ''}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary task-checklist-remove"
                          onClick={() => handleRemoveChecklistItem(index)}
                          disabled={loading}
                          aria-label={`Remove checklist item ${index + 1}`}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ol>
                )}

                {errors.checklistItems && <span className="error-text">{errors.checklistItems}</span>}
              </div>

              <div className="form-group">
                <div className="task-attachments-header">
                  <div>
                    <label htmlFor={getFormFieldId('attachments')}>Attachments</label>
                    <span className="help-text">
                      Add up to {MAX_ATTACHMENT_FILES} files, {MAX_ATTACHMENT_FILE_SIZE_LABEL} each. Changes upload when you save this task.
                    </span>
                  </div>
                  <span className="task-comments-count" aria-label={`${totalAttachmentCount} of ${MAX_ATTACHMENT_FILES} attachment slots used`}>
                    {totalAttachmentCount}/{MAX_ATTACHMENT_FILES}
                  </span>
                </div>

                <input
                  ref={attachmentInputRef}
                  type="file"
                  id={getFormFieldId('attachments')}
                  multiple
                  onChange={handleAttachmentSelection}
                  disabled={loading}
                  className={`task-attachments-input${attachmentError ? ' error-input' : ''}`}
                />

                {attachmentError && <span className="error-text">{attachmentError}</span>}

                {visibleAttachments.length > 0 && (
                  <>
                    <p className="task-attachments-group-label">Saved attachments</p>
                    <ul className="task-attachment-list">
                      {visibleAttachments.map((attachment) => (
                        <li key={attachment.id} className="task-attachment-item">
                          <div className="task-attachment-main">
                            <span className="task-attachment-name">{attachment.fileName}</span>
                            <span className="task-attachment-meta">
                              {formatAttachmentSize(attachment.fileSizeBytes)}
                              {attachment.uploadedAt ? ` · Added ${formatDateTime(attachment.uploadedAt)}` : ''}
                            </span>
                          </div>
                          <div className="task-attachment-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleMarkAttachmentForRemoval(attachment)}
                              disabled={loading}
                            >
                              Remove on save
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {pendingAttachments.length > 0 && (
                  <>
                    <p className="task-attachments-group-label">Pending uploads</p>
                    <ul className="task-attachment-list">
                      {pendingAttachments.map((file, index) => (
                        <li key={`${file.name}-${file.lastModified}-${index}`} className="task-attachment-item is-pending">
                          <div className="task-attachment-main">
                            <span className="task-attachment-name">{file.name}</span>
                            <span className="task-attachment-meta">{formatAttachmentSize(file.size)} · Upload after save</span>
                          </div>
                          <div className="task-attachment-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleRemovePendingAttachment(index)}
                              disabled={loading}
                            >
                              Remove
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {attachmentsMarkedForRemoval.length > 0 && (
                  <>
                    <p className="task-attachments-group-label">Queued for removal</p>
                    <ul className="task-attachment-list">
                      {attachmentsMarkedForRemoval.map((attachment) => (
                        <li key={`remove-${attachment.id}`} className="task-attachment-item is-removing">
                          <div className="task-attachment-main">
                            <span className="task-attachment-name">{attachment.fileName}</span>
                            <span className="task-attachment-meta">Will be deleted when you save.</span>
                          </div>
                          <div className="task-attachment-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleUndoAttachmentRemoval(attachment.id)}
                              disabled={loading}
                            >
                              Undo remove
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {totalAttachmentCount === 0 && attachmentsMarkedForRemoval.length === 0 && (
                  <p className="task-comments-status">No attachments selected.</p>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : isEditMode ? 'Update Task' : 'Create Task'}
                </button>

                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isBusy}>
                  Cancel
                </button>

                {isEditMode && (
                  <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={loading}>
                    Delete Task
                  </button>
                )}
              </div>
            </form>

            {isEditMode && taskDetails && (
              <section className="task-detail-section task-comments-section">
                <div className="task-comments-header">
                  <div>
                    <p className="task-detail-eyebrow">Comments</p>
                    <h3 className="task-comments-title">Notes and activity</h3>
                  </div>
                  <span className="task-comments-count" aria-label={`${comments.length} comments`}>
                    {comments.length}
                  </span>
                </div>

                {commentError && <div className="error-message">{commentError}</div>}

                <form className="task-comment-form" onSubmit={handleAddComment}>
                  <label className="task-comment-label" htmlFor={`${titleId}-new-comment`}>
                    New comment
                  </label>
                  <textarea
                    id={`${titleId}-new-comment`}
                    className="task-comment-textarea"
                    value={newCommentText}
                    onChange={(event) => setNewCommentText(event.target.value)}
                    rows={3}
                    maxLength={MAX_COMMENT_LENGTH}
                    placeholder="Add a comment"
                    disabled={commentSubmitting || loading}
                  />
                  <div className="task-comment-form-footer">
                    <span className="task-comment-help">{newCommentText.length}/{MAX_COMMENT_LENGTH}</span>
                    <button type="submit" className="btn-primary" disabled={commentSubmitting || loading}>
                      Add comment
                    </button>
                  </div>
                </form>

                {comments.length === 0 ? (
                  <p className="task-comments-status">No comments yet.</p>
                ) : (
                  <ol className="task-comment-list">
                    {comments.map((comment) => {
                      const isEditing = editingCommentId === comment.id;

                      return (
                        <li key={comment.id} className="task-comment-item">
                          <div className="task-comment-meta">
                            <span className="task-comment-timestamp">{formatDateTime(getCommentTimestamp(comment))}</span>
                            <div className="task-comment-actions">
                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => void handleSaveComment(comment.id)}
                                    disabled={commentSubmitting}
                                  >
                                    Save comment changes
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => {
                                      setEditingCommentId(null);
                                      setEditingText('');
                                      setCommentError(null);
                                    }}
                                    disabled={commentSubmitting}
                                  >
                                    Cancel comment editing
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => {
                                      setEditingCommentId(comment.id);
                                      setEditingText(comment.text);
                                      setCommentError(null);
                                    }}
                                    disabled={commentSubmitting}
                                  >
                                    Edit comment
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => void handleDeleteComment(comment.id)}
                                    disabled={commentSubmitting}
                                  >
                                    Delete comment
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {isEditing ? (
                            <div className="task-comment-editing">
                              <label className="task-comment-label" htmlFor={`${titleId}-edit-comment-${comment.id}`}>
                                Edit comment
                              </label>
                              <textarea
                                id={`${titleId}-edit-comment-${comment.id}`}
                                className="task-comment-textarea"
                                value={editingText}
                                onChange={(event) => setEditingText(event.target.value)}
                                rows={3}
                                maxLength={MAX_COMMENT_LENGTH}
                                disabled={commentSubmitting}
                              />
                            </div>
                          ) : (
                            <p className="task-comment-text">{comment.text}</p>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaskFormModal;